import api from './api';
import { Tender, LeadStatus } from '../types';

export interface TenderAssignmentInput {
  brand_id?: number | null;
  user_id: number;
  status?: LeadStatus;
}

export interface CreateTenderData {
  date: string;
  awarder?: string;
  awardee_id?: number;
  awardee_name?: string;
  awardee_city?: string;
  awardee_phone?: string;
  awardee_email?: string | null;
  work_order_value?: number;
  industry?: string;
  city?: string;
  scope_of_work?: string;
  completion_months?: number;
  product_req?: string;
  qty_req?: string;
  brands_in_use?: string;
  price?: number;
  source?: string | null;
  update_status?: string;
  assignments: TenderAssignmentInput[];
}

export interface UpdateTenderData {
  date?: string;
  awarder?: string;
  awardee_id?: number;
  awardee_name?: string;
  awardee_city?: string;
  awardee_phone?: string;
  awardee_email?: string | null;
  work_order_value?: number;
  industry?: string;
  city?: string;
  scope_of_work?: string;
  completion_months?: number;
  product_req?: string;
  qty_req?: string;
  brands_in_use?: string;
  price?: number;
  source?: string | null;
  update_status?: string;
  assignments?: TenderAssignmentInput[];
  conversion_status?: 'not_ready' | 'ready' | 'converted';
  ready_for_conversion?: boolean;
  next_call_date?: string;
}

export interface TenderFilters {
  status?: LeadStatus;
  assigned_to?: number;
  awardee_id?: number;
  brand_id?: number;
  source?: string;
  source_empty?: boolean;
  last_call_outcome?: string;
  conversion_status?: 'not_ready' | 'ready' | 'converted';
  ready_for_conversion?: boolean;
  search?: string;
  next_call_due?: 'today' | 'overdue' | 'upcoming';
  call_date_from?: string;
  call_date_to?: string;
  page?: number;
  per_page?: number;
}

export interface TenderAwardeeFilterOption {
  id: number;
  name: string;
}

export interface TenderListFilterOptions {
  call_outcomes: string[];
  sources?: string[];
  awardees?: TenderAwardeeFilterOption[];
}

export interface TenderListResponse {
  data: Tender[];
  total: number;
}

export interface BulkActionResponse {
  total_selected: number;
  updated_count: number;
  failed_count: number;
  errors: string[];
}

export const tenderService = {
  getFilterOptions: async (): Promise<TenderListFilterOptions> => {
    const response = await api.get('/api/tenders/filter-options');
    return response.data;
  },

  getAll: async (filters?: TenderFilters): Promise<TenderListResponse> => {
    const response = await api.get('/api/tenders', { params: filters });
    return response.data;
  },

  getById: async (id: number): Promise<Tender> => {
    const response = await api.get(`/api/tenders/${id}`);
    return response.data;
  },

  create: async (data: CreateTenderData): Promise<{ message?: string; id?: number }> => {
    const response = await api.post('/api/tenders', data);
    return response.data;
  },

  update: async (id: number, data: UpdateTenderData): Promise<void> => {
    await api.put(`/api/tenders/${id}`, data);
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/tenders/${id}`);
  },

  bulkDelete: async (tenderIds: number[]): Promise<BulkActionResponse> => {
    const response = await api.post('/api/tenders/bulk-delete', { tender_ids: tenderIds });
    return response.data;
  },

  bulkAssign: async (
    tenderIds: number[],
    assignments: TenderAssignmentInput[]
  ): Promise<BulkActionResponse> => {
    const response = await api.post('/api/tenders/bulk-assign', {
      tender_ids: tenderIds,
      assignments
    });
    return response.data;
  },

  bulkUnassign: async (
    tenderIds: number[],
    assignments: Pick<TenderAssignmentInput, 'brand_id' | 'user_id'>[]
  ): Promise<BulkActionResponse> => {
    const response = await api.post('/api/tenders/bulk-unassign', {
      tender_ids: tenderIds,
      assignments
    });
    return response.data;
  },

  deleteAssignment: async (tenderId: number, assignmentId: number): Promise<void> => {
    await api.delete(`/api/tenders/${tenderId}/assignments/${assignmentId}`);
  },

  bulkStatus: async (tenderIds: number[], status: LeadStatus): Promise<BulkActionResponse> => {
    const response = await api.post('/api/tenders/bulk-status', { tender_ids: tenderIds, status });
    return response.data;
  },

  markReadyForConversion: async (id: number): Promise<void> => {
    await api.post(`/api/tenders/${id}/mark-ready`);
  },

  convertToCustomer: async (
    id: number,
    data: {
      brand_id?: number;
      status?: string;
      customer_code?: string;
      sap_id?: string;
      work_order_value?: number;
      notes?: string;
      conversion_action?: 'add_location' | 'create_customer';
      sales_officer_id?: number;
    }
  ): Promise<{ customer_id: number; customer: unknown; reused?: boolean; message?: string }> => {
    const response = await api.post(`/api/tenders/${id}/convert-to-customer`, data);
    return response.data;
  },

  checkMatchingCustomer: async (
    id: number,
    brandId: number
  ): Promise<{
    exists: boolean;
    existing_customer_id?: number;
    existing_customer_code?: string | null;
    existing_tender_id?: number | null;
    message?: string;
  }> => {
    const response = await api.get(`/api/tenders/${id}/matching-customer`, { params: { brand_id: brandId } });
    return response.data;
  },
};
